import api from './api';

class InstrumentsService {
  constructor() {
    this.instruments = [];
    this.categories = {};
    this.instrumentMap = new Map();
    this.categoryMap = new Map();
    this.loading = false;
    this.error = null;
  }

  /**
   * Fetch all instruments from backend
   */
  async fetchInstruments(category = null) {
    try {
      this.loading = true;
      this.error = null;

      const params = {};
      if (category) params.category = category;

      const response = await api.get('/market/instruments', { params });
      
      this.instruments = response.data.instruments || [];
      this.categories = response.data.categories || {};

      // Build lookup maps
      this.instrumentMap.clear();
      this.instruments.forEach(inst => {
        this.instrumentMap.set(inst.id, inst);
      });

      // Build category lookup
      this.categoryMap.clear();
      Object.keys(this.categories).forEach(cat => {
        this.categoryMap.set(cat, this.instruments.filter(i => i.category === cat));
      });

      return {
        instruments: this.instruments,
        categories: this.categories
      };
    } catch (error) {
      this.error = error.message;
      console.error('Failed to fetch instruments:', error);
      throw error;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Get single instrument by ID
   */
  async getInstrument(id) {
    try {
      // Check local cache first
      if (this.instrumentMap.has(id)) {
        return this.instrumentMap.get(id);
      }

      // Fetch from backend
      const response = await api.get(`/market/instruments/${id}`);
      const instrument = response.data;
      
      // Cache it
      this.instrumentMap.set(id, instrument);
      
      return instrument;
    } catch (error) {
      console.error(`Failed to fetch instrument ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get all instruments
   */
  getAll() {
    return this.instruments;
  }

  /**
   * Get instruments by category
   */
  getByCategory(category) {
    return this.categoryMap.get(category) || [];
  }

  /**
   * Get all categories
   */
  getCategories() {
    return Object.keys(this.categories);
  }

  /**
   * Get category configuration
   */
  getCategoryConfig(category) {
    return this.categories[category] || {};
  }

  /**
   * Search instruments by name or symbol
   */
  search(query) {
    const q = query.toLowerCase();
    return this.instruments.filter(inst => 
      inst.id.toLowerCase().includes(q) ||
      inst.name.toLowerCase().includes(q)
    );
  }

  /**
   * Get format precision for a category
   */
  getDecimalPlaces(category) {
    const config = this.getCategoryConfig(category);
    return config.decimals || 2;
  }

  /**
   * Format price based on category
   */
  formatPrice(price, category) {
    const decimals = this.getDecimalPlaces(category);
    return price.toFixed(decimals);
  }

  /**
   * Get lot size for category
   */
  getLotSize(category) {
    const config = this.getCategoryConfig(category);
    return config.lot_size || 1;
  }

  /**
   * Calculate cost based on price, lots, and category
   */
  calculateCost(price, lots, category) {
    const lotSize = this.getLotSize(category);
    return price * lots * lotSize;
  }

  /**
   * Check if instruments are loaded
   */
  isLoaded() {
    return this.instruments.length > 0;
  }

  /**
   * Get loading state
   */
  getLoadingState() {
    return {
      loading: this.loading,
      error: this.error,
      loaded: this.isLoaded()
    };
  }
}

// Create singleton instance
const instrumentsService = new InstrumentsService();

export default instrumentsService;
